/**
 * Modales Individuales de Redes Sociales y WhatsApp — AviFácil Design System
 * Diseñados manteniendo la identidad, color y personalidad única de cada red/canal.
 * Reutilizables globalmente en cualquier vista (registro, login, perfil, ayuda, etc.).
 */

'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  Copy,
  ExternalLink,
  X,
  BadgeCheck,
  MessageCircle,
} from 'lucide-react';
import styles from './SocialIndividualModal.module.css';

export type RedSocialId =
  | 'TODAS'
  | 'INSTAGRAM'
  | 'FACEBOOK'
  | 'YOUTUBE'
  | 'TIKTOK'
  | 'GOOGLE'
  | 'WHATSAPP';

export interface SocialConfig {
  id: RedSocialId;
  title: string;
  subtitle: string;
  badgeLabel: string;
  description: string;
  ctaText: string;
  url: string;
  displayUrl: string;
  bgIcon: string;
  icon: React.ReactNode;
}

export const CANALES_CONFIG: Record<Exclude<RedSocialId, 'TODAS'>, SocialConfig> = {
  INSTAGRAM: {
    id: 'INSTAGRAM',
    title: 'Instagram AviFácil',
    subtitle: '@avifacil_oficial',
    badgeLabel: 'Cuenta Oficial',
    description:
      'Acompáñanos en Instagram para ver fotos de granjas reales, casos de éxito de avicultores, tips zootécnicos diarios y actualizaciones exclusivas de nuestra app.',
    ctaText: 'Abrir Instagram',
    url: 'https://instagram.com/avifacil_oficial',
    displayUrl: 'instagram.com/avifacil_oficial',
    bgIcon: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
    icon: (
      <svg className="w-6 h-6 fill-current text-white" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
  },
  FACEBOOK: {
    id: 'FACEBOOK',
    title: 'Facebook AviFácil',
    subtitle: 'AviFácil — Gestión Avícola',
    badgeLabel: 'Página Oficial',
    description:
      'Únete a nuestra comunidad avícola en Facebook. Participa en transmisiones en vivo, debates sobre bioseguridad y comparte experiencias con productores de toda la región.',
    ctaText: 'Abrir Facebook',
    url: 'https://facebook.com/avifacil',
    displayUrl: 'facebook.com/avifacil',
    bgIcon: '#1877F2',
    icon: (
      <svg className="w-6 h-6 fill-current text-white" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  YOUTUBE: {
    id: 'YOUTUBE',
    title: 'YouTube AviFácil',
    subtitle: 'AviFácil Oficial',
    badgeLabel: 'Canal Oficial',
    description:
      'Aprende con nuestras guías en video: configuración de tablas de alimentación, cálculo de conversión alimenticia, manejo de cortinas y testimonios de granjas tecnificadas.',
    ctaText: 'Ver Canal en YouTube',
    url: 'https://youtube.com/@avifacil',
    displayUrl: 'youtube.com/@avifacil',
    bgIcon: '#FF0000',
    icon: (
      <svg className="w-6 h-6 fill-current text-white" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  TIKTOK: {
    id: 'TIKTOK',
    title: 'TikTok AviFácil',
    subtitle: '@avifacil',
    badgeLabel: 'Cuenta Oficial',
    description:
      'Consejos cortos de 60 segundos sobre sanidad avícola, control de pesajes, bioseguridad en galpones y la vida en la granja explicada de forma fácil y entretenida.',
    ctaText: 'Ver Videos en TikTok',
    url: 'https://tiktok.com/@avifacil',
    displayUrl: 'tiktok.com/@avifacil',
    bgIcon: '#000000',
    icon: (
      <svg className="w-5 h-5 fill-current text-white" viewBox="0 0 24 24">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.64 1.82 2.89 3.5 2.77 1.45-.04 2.76-.98 3.19-2.36.14-.48.2-1 .2-1.51.01-5.77.01-11.53.01-17.3z" />
      </svg>
    ),
  },
  GOOGLE: {
    id: 'GOOGLE',
    title: 'Google & Reseñas AviFácil',
    subtitle: 'AviFácil Tecnologías Avícolas',
    badgeLabel: 'Perfil Verificado',
    description:
      'Conoce las opiniones y calificaciones de avicultores reales que ya gestionan sus lotes con nosotros. Califica nuestra plataforma y déjanos tus comentarios.',
    ctaText: 'Ver Perfil en Google',
    url: 'https://google.com/search?q=AviFacil',
    displayUrl: 'google.com/search?q=AviFacil',
    bgIcon: '#ffffff',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.65v3.03h3.88c2.27-2.09 3.665-5.17 3.665-9.12z"
        />
        <path
          fill="#34A853"
          d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.03c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.27v3.13C3.25 21.31 7.31 24 12 24z"
        />
        <path
          fill="#FBBC05"
          d="M5.28 14.29c-.25-.72-.38-1.49-.38-2.29s.13-1.57.38-2.29V6.58H1.27C.46 8.2.01 10.04.01 12s.45 3.8 1.26 5.42l4.01-3.13z"
        />
        <path
          fill="#EA4335"
          d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.25 2.69 1.27 6.58l4.01 3.13c.95-2.83 3.6-4.96 6.72-4.96z"
        />
      </svg>
    ),
  },
  WHATSAPP: {
    id: 'WHATSAPP',
    title: 'WhatsApp Oficial AviFácil',
    subtitle: '+57 300 000 0000',
    badgeLabel: 'Línea Directa 24/7',
    description:
      '¿Tienes dudas sobre tu registro, acceso a tu granja o asistencia técnica? Escríbenos directamente y nuestro equipo te responderá en minutos.',
    ctaText: 'Iniciar Chat en WhatsApp',
    url: 'https://wa.me/573000000000?text=Hola%20AviF%C3%A1cil,%20necesito%20ayuda%20con%20mi%20granja',
    displayUrl: 'wa.me/573000000000',
    bgIcon: '#25D366',
    icon: (
      <svg className="w-6 h-6 fill-current text-white" viewBox="0 0 24 24">
        <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.312.045-.694.06-2.006-.484-1.579-.655-2.613-2.257-2.695-2.366-.08-.109-.646-.86-.646-1.639 0-.779.408-1.163.553-1.32.145-.157.319-.196.425-.196.106 0 .213.001.305.006.1.005.234-.038.366.279.136.327.464 1.132.505 1.215.041.083.068.18.014.288-.053.109-.08.177-.16.27-.08.093-.169.208-.241.28-.08.08-.163.167-.07.327.093.16.413.682.887 1.103.61.543 1.124.71 1.284.79.16.08.254.068.349-.041.095-.109.407-.474.516-.638.109-.164.218-.136.368-.082.15.054.954.45 1.118.532.164.082.273.123.313.191.041.069.041.402-.103.807zM12 2C6.477 2 2 6.477 2 12c0 1.891.527 3.66 1.443 5.176L2 22l4.981-1.306A9.957 9.957 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18.167c-1.697 0-3.272-.51-4.585-1.385l-.329-.22-2.96.776.79-2.885-.24-.383A8.134 8.134 0 013.833 12c0-4.503 3.664-8.167 8.167-8.167 4.503 0 8.167 3.664 8.167 8.167 0 4.503-3.664 8.167-8.167 8.167z" />
      </svg>
    ),
  },
};

export interface SocialIndividualModalProps {
  isOpen: boolean;
  onClose: () => void;
  network: RedSocialId;
  customUrl?: string;
  customMessage?: string;
}

export function SocialIndividualModal({
  isOpen,
  onClose,
  network,
  customUrl,
  customMessage,
}: SocialIndividualModalProps) {
  const [mounted, setMounted] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !mounted) return null;

  const config = (network !== 'TODAS' ? CANALES_CONFIG[network] : null) || CANALES_CONFIG.INSTAGRAM;
  const targetUrl = customUrl || config.url;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(targetUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // Fallback
    }
  };

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={config.title}
    >
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        {/* Botón Cerrar (X) */}
        <button
          type="button"
          onClick={onClose}
          className={styles.closeBtn}
          aria-label="Cerrar modal"
        >
          <X size={17} />
        </button>

        {/* Encabezado con Icono Individual */}
        <div className={styles.headerRow}>
          <div
            className={styles.iconCircle}
            style={{
              background: config.bgIcon,
              border: network === 'GOOGLE' ? '1px solid #e2e8f0' : 'none',
            }}
          >
            {config.icon}
          </div>
          <div className={styles.headerInfo}>
            <div className={styles.titleRow}>
              <h2 className={styles.title}>{config.title}</h2>
              <span className={styles.verifiedBadge} title="Verificado">
                <BadgeCheck size={16} className="fill-sky-500 text-white" />
              </span>
            </div>
            <span className={styles.subtitle}>{config.subtitle}</span>
          </div>
        </div>

        {/* Descripción y Enlace */}
        <div className={styles.descriptionBox}>
          <p className={styles.descriptionText}>
            {customMessage || config.description}
          </p>

          <div className={styles.linkBox}>
            <span className={styles.linkUrl}>{config.displayUrl}</span>
            <button
              type="button"
              onClick={handleCopy}
              className={styles.copyBtn}
              title="Copiar enlace"
            >
              {copied ? (
                <>
                  <Check size={12} className="text-emerald-600" />
                  <span className="text-emerald-700">¡Copiado!</span>
                </>
              ) : (
                <>
                  <Copy size={12} />
                  <span>Copiar</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Botones de Acción */}
        <div className={styles.actionsRow}>
          <a
            href={targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.primaryCtaBtn}
            style={{
              background:
                network === 'INSTAGRAM'
                  ? 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)'
                  : network === 'FACEBOOK'
                  ? '#1877F2'
                  : network === 'YOUTUBE'
                  ? '#FF0000'
                  : network === 'TIKTOK'
                  ? '#111827'
                  : network === 'GOOGLE'
                  ? '#1f2937'
                  : '#25D366',
            }}
          >
            <span>{config.ctaText}</span>
            <ExternalLink size={15} />
          </a>

          <button
            type="button"
            onClick={onClose}
            className={styles.secondaryBtn}
          >
            Cerrar
          </button>
        </div>

        {/* Toast Notificación */}
        {copied && (
          <div className={styles.toastPill} role="status">
            <Check size={14} className="text-emerald-400" />
            <span>Enlace copiado al portapapeles</span>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── Exportaciones Específicas Individuales ───
export function InstagramModal(props: Omit<SocialIndividualModalProps, 'network'>) {
  return <SocialIndividualModal {...props} network="INSTAGRAM" />;
}

export function FacebookModal(props: Omit<SocialIndividualModalProps, 'network'>) {
  return <SocialIndividualModal {...props} network="FACEBOOK" />;
}

export function YouTubeModal(props: Omit<SocialIndividualModalProps, 'network'>) {
  return <SocialIndividualModal {...props} network="YOUTUBE" />;
}

export function TikTokModal(props: Omit<SocialIndividualModalProps, 'network'>) {
  return <SocialIndividualModal {...props} network="TIKTOK" />;
}

export function GoogleModal(props: Omit<SocialIndividualModalProps, 'network'>) {
  return <SocialIndividualModal {...props} network="GOOGLE" />;
}

export function WhatsAppModal(props: Omit<SocialIndividualModalProps, 'network'>) {
  return <SocialIndividualModal {...props} network="WHATSAPP" />;
}
